import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { applyTheme, readTheme, type Theme } from '../lib/theme'
import { dismissToast, errorInfoOf, markErrorShown, pushScreenToast, referenceLine, subscribeToasts, type ToastItem } from '../lib/feedback'
import { useCountUp } from './kit/motion'

// The small visual vocabulary every Phase 1 screen shares: skeletons while a
// screen loads, numbers that count up, a score bar, a donut for four or five
// slices, a completion ring, initials for a name, and a toast for what just
// happened. All CSS-driven; the only script is the count-up, which respects
// prefers-reduced-motion by jumping straight to the value.

export function Skeleton({ lines = 3, height = 14, width }: { lines?: number; height?: number; width?: string }) {
  return (
    <div className="sk" aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <span key={i} className="sk-line" style={{ height, width: width ?? (i === lines - 1 ? '60%' : '100%') }} />
      ))}
    </div>
  )
}

export function SkeletonTiles({ n = 5 }: { n?: number }) {
  return (
    <div className="p1-tiles" aria-hidden="true">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="p1-tile sk-tile">
          <span className="sk-line" style={{ height: 30, width: '40%' }} />
          <span className="sk-line" style={{ height: 12, width: '70%', marginTop: 8 }} />
        </div>
      ))}
    </div>
  )
}

// The count-up lives in the kit now, with the reduced-motion hook it shares.
// It is re-exported under the same name so every screen that already imports
// it from here keeps working unchanged.
export { useCountUp }

export function Count({ value }: { value: number }) {
  const v = useCountUp(value)
  return <>{v}</>
}

// A horizontal score bar. Bars and badges, not radial gauges: a bar is read
// accurately and its colour is never the only signal because the number sits
// beside it.
export function ScoreBar({ value, max = 100, tone = 'blue', label }: { value: number; max?: number; tone?: 'blue' | 'low' | 'med' | 'high' | 'crit'; label?: string }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100))
  return (
    <div className="scorebar" role="img" aria-label={(label ? label + ': ' : '') + Math.round(value) + ' of ' + max}>
      <div className={'scorebar-track tone-' + tone}>
        <div className="scorebar-fill" style={{ width: pct + '%' }} />
      </div>
    </div>
  )
}

export type Slice = { label: string; value: number; tone: string }

// A donut for four or five slices with the legend beside it, because that is
// how many a person can compare, and the numbers are in the legend rather
// than guessed from arc lengths.
export function Donut({ slices, size = 120, thickness = 16, centre }: { slices: Slice[]; size?: number; thickness?: number; centre?: ReactNode }) {
  const total = slices.reduce((a, s) => a + s.value, 0)
  const r = (size - thickness) / 2
  const c = 2 * Math.PI * r
  let offset = 0
  return (
    <div className="donut">
      <svg width={size} height={size} viewBox={'0 0 ' + size + ' ' + size} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bar-track)" strokeWidth={thickness} />
        {total > 0 &&
          slices.map((s) => {
            const len = (s.value / total) * c
            const el = (
              <circle
                key={s.label}
                className={'donut-arc tone-' + s.tone}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                strokeWidth={thickness}
                strokeDasharray={len + ' ' + (c - len)}
                strokeDashoffset={-offset}
                transform={'rotate(-90 ' + size / 2 + ' ' + size / 2 + ')'}
              />
            )
            offset += len
            return el
          })}
      </svg>
      <div className="donut-centre">{centre}</div>
      <ul className="donut-legend">
        {slices.map((s) => (
          <li key={s.label}>
            <span className={'swatch tone-' + s.tone} aria-hidden="true" />
            <span className="lbl">{s.label}</span>
            <span className="num">{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function Ring({ done, total, size = 56 }: { done: number; total: number; size?: number }) {
  const r = (size - 6) / 2
  const c = 2 * Math.PI * r
  const pct = total === 0 ? 0 : done / total
  return (
    <svg className="ring" width={size} height={size} viewBox={'0 0 ' + size + ' ' + size} role="img" aria-label={done + ' of ' + total + ' done'}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bar-track)" strokeWidth={6} />
      <circle
        className="ring-fill"
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--blue)"
        strokeWidth={6}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        transform={'rotate(-90 ' + size / 2 + ' ' + size / 2 + ')'}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="ring-text">
        {done}/{total}
      </text>
    </svg>
  )
}

// The lightness is 31 and not 40 because the initials are white on top of it.
// At 40 the pair measures about 3.8 to 1, which is under the 4.5 a small
// label needs; at 31 it clears it for every hue the generator can pick.
export function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const initials = ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '')).toUpperCase() || '?'
  let h = 0
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 360
  return (
    <span className="avatar" style={{ width: size, height: size, fontSize: Math.max(12, Math.round(size * 0.38)), background: 'hsl(' + h + ' 45% 31%)' }} aria-hidden="true">
      {initials}
    </span>
  )
}

// A reason, folded away.
//
// # WHY THIS EXISTS RATHER THAN A DELETION
//
// The chief product officer's verdict was that the product is text-heavy and
// cluttered, and she is right about the screens. She is not asking for the
// reasoning to be destroyed: a settings screen where somebody is choosing who
// has to sign off a critical vendor is exactly where the reason for a rule
// earns its keep, and a person meeting that rule for the first time has to be
// able to find out what it does.
//
// So the text stays and the screen does not carry it. Closed by default, one
// word wide, and the whole reason is one press away. Nothing is hidden from a
// screen reader either: this is a real disclosure element, so the text is in
// the document and announced as collapsed rather than removed from it.

export function Why({ children, label = 'Why' }: { children: ReactNode; label?: string }) {
  return (
    <details className="why">
      <summary>{label}</summary>
      <div className="why-body">{children}</div>
    </details>
  )
}

export function Icon({ name, size = 18 }: { name: string; size?: number }) {
  const p: Record<string, ReactNode> = {
    home: <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />,
    vendors: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18" /></>,
    assessments: <><path d="M9 4h6l1 2h3v14H5V6h3z" /><path d="M9 12l2 2 4-4" /></>,
    reports: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></>,
    plus: <path d="M12 5v14M5 12h14" />,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
    send: <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" />,
    check: <path d="m5 12 5 5L20 7" />,
    alert: <><path d="M12 3 2 21h20z" /><path d="M12 10v5M12 18h.01" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    bell: <><path d="M18 8a6 6 0 1 0-12 0c0 7-2 9-2 9h16s-2-2-2-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></>,
    // My work: a tray, the one list of what is waiting for the reader.
    inbox: <><path d="M3 13h5l1.5 3h5L16 13h5" /><path d="M5.5 5h13L21 13v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6z" /></>,
    user: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    shield: <><path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" /><path d="m9 12 2 2 4-4" /></>,
    file: <><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4M9 13h6M9 17h6" /></>,
    logout: <><path d="M15 4h4v16h-4M10 17l5-5-5-5M15 12H3" /></>,
    sparkle: <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.8 2.8M14.9 14.9l2.8 2.8M6.3 17.7l2.8-2.8M14.9 9.1l2.8-2.8" />,
    chevron: <path d="m9 6 6 6-6 6" />,
    // The side menu's button under 1024 px, and the way out of it.
    menu: <path d="M4 6h16M4 12h16M4 18h16" />,
    close: <path d="M6 6l12 12M18 6 6 18" />,
    // A link that opens another product in a new tab (the header's suite links).
    external: <><path d="M14 4h6v6M20 4l-9 9" /><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></>,
    // Copying an error's code and reference for support.
    copy: <><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a1 1 0 0 1 1-1h10" /></>,
    sun: (
      <>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
      </>
    ),
    moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />,
    auto: (
      <>
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <path d="M8 20h8M12 16v4" />
      </>
    ),
  }
  return (
    <svg className="icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p[name] ?? null}
    </svg>
  )
}

// Toasts: one line about what just happened, bottom right, gone in a few
// seconds (twelve for a failure). Never the only place an outcome is shown.
//
// Since release 3.1 the toasts live in lib/feedback.ts, which is also the net
// that makes sure every change the console sends says whether it worked: a
// screen's own toast goes through it (so a failure's toast gains the code and
// the reference, and is never doubled), and the net raises one where the
// screen said nothing. useToast keeps the signature every screen already
// calls.
export function useToast(): (text: string, tone?: 'ok' | 'warn') => void {
  return pushScreenToast
}

// What support needs from a failure: its code and the reference of the one
// request that failed, with a button that copies both.
export function ErrorReference({ code, reference }: { code: string; reference: string }) {
  const [copied, setCopied] = useState(false)
  const line = referenceLine(code, reference)
  return (
    <p className="error-ref" data-testid="error-reference">
      <span>
        Error code: <span className="mono">{code}</span>
      </span>
      <span>
        Reference: <span className="mono">{reference || 'No reference'}</span>
      </span>
      <button
        type="button"
        className="error-ref-copy"
        onClick={() => {
          void navigator.clipboard?.writeText(line).then(
            () => setCopied(true),
            () => setCopied(false),
          )
        }}
        title="Copy the code and the reference for support"
      >
        <Icon name="copy" size={13} /> {copied ? 'Copied' : 'Copy'}
      </button>
    </p>
  )
}

// The same line for a failure a screen shows in place, which also tells the
// feedback net it is shown, so no toast repeats it.
export function ErrorReferenceOf({ error }: { error: unknown }) {
  markErrorShown(error)
  const info = errorInfoOf(error)
  return <ErrorReference code={info.code} reference={info.reference} />
}

export function ToastView({ t }: { t: ToastItem }) {
  return (
    <div
      className={'toast toast-' + t.kind}
      role={t.kind === 'warn' ? 'alert' : 'status'}
      data-testid={t.kind === 'warn' ? 'toast-error' : 'toast-ok'}
      data-fallback={t.fallback ? 'true' : undefined}
    >
      <Icon name={t.kind === 'ok' ? 'check' : 'alert'} size={16} />
      <div className="toast-body">
        {t.title && <strong className="toast-title">{t.title}</strong>}
        <span>{t.text}</span>
        {t.code && <ErrorReference code={t.code} reference={t.reference ?? ''} />}
      </div>
      <button type="button" className="toast-close" aria-label="Dismiss" onClick={() => dismissToast(t.id)}>
        &times;
      </button>
    </div>
  )
}

export function ToastHost({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  useEffect(() => subscribeToasts(setItems), [])
  // On the body, beside the app and not inside it: the app is inert while a
  // dialog is open, and a toast raised then has to be read, copied from and
  // dismissed. data-above-dialogs is what tells the dialog so (kit/Dialog.tsx).
  const host = (
    <div className="toasts" aria-live="polite" data-above-dialogs="">
      {items.map((t) => (
        <ToastView key={t.id} t={t} />
      ))}
    </div>
  )
  return (
    <>
      {children}
      {typeof document === 'undefined' ? host : createPortal(host, document.body)}
    </>
  )
}

// Auto, light, dark, and round again. The word is there so nobody has to
// decode a sun from a moon at 15 pixels.
export function ThemeButton() {
  const [theme, setTheme] = useState<Theme>(() => readTheme())
  const next: Record<Theme, Theme> = { system: 'light', light: 'dark', dark: 'system' }
  const word = theme === 'system' ? 'Auto' : theme === 'light' ? 'Light' : 'Dark'
  const icon = theme === 'system' ? 'auto' : theme === 'light' ? 'sun' : 'moon'
  const label = 'Theme: ' + word + '. Click to switch.'
  return (
    <button
      type="button"
      className="theme-button"
      onClick={() => {
        const t = next[theme]
        applyTheme(t)
        setTheme(t)
      }}
      title={label}
      aria-label={label}
    >
      <Icon name={icon} size={15} /> {word}
    </button>
  )
}
